// @ts-strict-ignore
import type {
  ComponentProps,
  ComponentType,
  CSSProperties,
  ReactNode,
  Ref,
  SVGProps,
} from 'react';

import { Block } from '@actual-app/components/block';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { Tooltip } from '@actual-app/components/tooltip';
import { View } from '@actual-app/components/view';

import { ItemContent } from './ItemContent';
import { useSidebar } from './SidebarProvider';

type ItemProps = {
  title: string;
  Icon:
    | ComponentType<SVGProps<SVGElement>>
    | ComponentType<SVGProps<SVGSVGElement>>;
  to?: string;
  children?: ReactNode;
  style?: CSSProperties;
  indent?: number;
  onClick?: ComponentProps<typeof ItemContent>['onClick'];
  forceHover?: boolean;
  forceActive?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  dataTestId?: string;
};

export function Item({
  children,
  Icon,
  title,
  style,
  to,
  onClick,
  indent = 0,
  forceHover = false,
  forceActive = false,
  buttonRef,
  dataTestId,
}: ItemProps) {
  const sidebar = useSidebar();
  const isCollapsed = sidebar?.collapsed ?? false;

  const hoverStyle = {
    backgroundColor: theme.sidebarItemBackgroundHover,
  };

  if (isCollapsed) {
    const collapsedContent = (
      <View
        style={{
          width: 40,
          height: 36,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon width={18} height={18} />
      </View>
    );

    return (
      <View
        data-testid={dataTestId}
        style={{
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          ...style,
        }}
      >
        <Tooltip content={title} placement="right">
          <ItemContent
            buttonRef={buttonRef}
            title={title}
            style={{
              margin: '3px 0',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              color: theme.sidebarItemText,
              transition: 'background-color .15s ease-out, color .15s ease-out',
              ...(forceHover ? hoverStyle : {}),
              ':hover': hoverStyle,
            }}
            forceActive={forceActive}
            activeStyle={{
              backgroundColor: theme.sidebarItemBackgroundSelected,
              color: theme.sidebarItemTextSelected,
            }}
            to={to}
            onClick={onClick}
          >
            {collapsedContent}
          </ItemContent>
        </Tooltip>
      </View>
    );
  }

  const content = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: 20,
      }}
    >
      <Icon width={15} height={15} />
      <Block style={{ marginLeft: 8 }}>{title}</Block>
      <View style={{ flex: 1 }} />
    </View>
  );

  return (
    <View data-testid={dataTestId} style={{ flexShrink: 0, ...style }}>
      <ItemContent
        buttonRef={buttonRef}
        title={title}
        style={{
          ...styles.mediumText,
          margin: '2px 8px',
          borderRadius: 6,
          paddingTop: 7,
          paddingBottom: 7,
          paddingLeft: 12 + indent,
          paddingRight: 12,
          textDecoration: 'none',
          color: theme.sidebarItemText,
          transition: 'background-color .15s ease-out, color .15s ease-out',
          ...(forceHover ? hoverStyle : {}),
          ':hover': hoverStyle,
        }}
        forceActive={forceActive}
        activeStyle={{
          backgroundColor: theme.sidebarItemBackgroundSelected,
          color: theme.sidebarItemTextSelected,
          fontWeight: 600,
        }}
        to={to}
        onClick={onClick}
      >
        {content}
      </ItemContent>
      {children ? <View style={{ marginTop: 5 }}>{children}</View> : null}
    </View>
  );
}
